/**
 * SystemBase - Base class for all game systems
 * Provides common functionality: event handling, lifecycle management, debug logging
 * All game systems should extend this class
 */

import { EventBus, EVENTS } from '../../shared/EventBus';
import type { Unsubscribe, EventName, EmitArgs, EventHandler } from '../../shared/EventBus';

export interface ISystem {
  readonly id: string;
  initialize(config?: any): Promise<void> | void;
  destroy(): void;
}

export abstract class SystemBase implements ISystem {
  public abstract readonly id: string;

  protected readonly eventBus = EventBus.getInstance();
  protected readonly subscriptions: Unsubscribe[] = [];
  protected isInitialized = false;
  protected debugMode = false;

  /**
   * Initialize the system
   * Override in subclass for specific initialization logic
   */
  initialize(_config?: any): Promise<void> | void {
    if (this.isInitialized) {
      this.log('warn', `System ${this.id} already initialized`);
      return;
    }

    this.isInitialized = true;
    this.log('info', `System ${this.id} initialized`);
  }

  /**
   * Subscribe to global event - auto-cleaned on destroy
   * @param event - Event name to subscribe to
   * @param handler - Callback function
   * @returns Unsubscribe function
   */
  protected subscribe<K extends EventName>(event: K, handler: EventHandler<K>): Unsubscribe {
    // Forward via the dynamic path — type-safety is enforced by THIS signature
    // (K extends EventName + EventHandler<K>); the bus call is an impl detail.
    const unsub = this.eventBus.onDynamic(event, handler as (...args: any[]) => void);
    this.subscriptions.push(unsub);
    return unsub;
  }

  /**
   * Subscribe once to global event
   * @param event - Event name to subscribe to
   * @param handler - Callback function (called once then auto-unsubscribed)
   */
  protected subscribeOnce<K extends EventName>(event: K, handler: EventHandler<K>): void {
    const unsub = this.eventBus.onceDynamic(event, handler as (...args: any[]) => void);
    this.subscriptions.push(unsub);
  }

  /**
   * Dynamic-key subscribe — auto-cleaned on destroy. For event names not (yet)
   * in EVENTS: a server-driven dynamic name, or a missing-wire listener whose
   * emitter-name alignment is a pending E3 fix. Use the typed `subscribe()`
   * for everything statically known.
   */
  protected subscribeDynamic(event: string, handler: (...args: any[]) => void): Unsubscribe {
    const unsub = this.eventBus.onDynamic(event, handler);
    this.subscriptions.push(unsub);
    return unsub;
  }

  /**
   * Emit global event
   * @param event - Event name
   * @param args - Event payload
   */
  protected emit<K extends EventName>(event: K, ...args: EmitArgs<K>): void {
    this.eventBus.emitDynamic(event, ...args);
  }

  /**
   * Enable/disable debug mode for this system
   */
  setDebug(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Log helper that includes system ID
   */
  protected log(level: 'info' | 'warn' | 'error' | 'debug', message: string, ...args: any[]): void {
    const prefix = `[${this.id}]`;
    switch (level) {
    case 'info':
      console.log(prefix, message, ...args);
      break;
    case 'warn':
      console.warn(prefix, message, ...args);
      break;
    case 'error':
      console.error(prefix, message, ...args);
      break;
    case 'debug':
      if (this.debugMode) {
        console.log(prefix, '[DEBUG]', message, ...args);
      }
      break;
    }
  }

  /**
   * Alias for destroy() to satisfy IGameSystem interface
   */
  dispose(): void {
    this.destroy();
  }

  /**
   * Clean up all subscriptions and resources
   * Override in subclass for additional cleanup
   */
  destroy(): void {
    // Unsubscribe all
    this.subscriptions.forEach(unsub => {
      try {
        unsub();
      } catch (err) {
        // Ignore errors during cleanup
      }
    });
    this.subscriptions.length = 0;

    this.isInitialized = false;
    this.log('info', `System ${this.id} destroyed`);
  }
}


// Re-export event constants for convenience
export { EVENTS, EventBus, Unsubscribe };
